import { loginRoute } from '@/interceptors/route'
import axios, { type InternalAxiosRequestConfig } from 'axios'
import { useUserStore } from '@/store'
import { mergeStep, showModal } from '.'
import { useDomain } from '@/hooks/useDomain'

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    hideErrorToast?: boolean
  }
}

const domainManager = useDomain({
  remote: false, // 是否远程获取域名，如果为false，则使用本地域名
})

const realnameJump = mergeStep(async (message) => {
  if (location.href.includes('verify/index')) {
    return
  }
  await showModal(message, {
    confirmText: '立即认证',
  })
  uni.reLaunch({
    url: '/pages-sub/verify/index',
  })
})

const http = axios.create({
  // baseURL 将在请求拦截器中动态设置
  headers: {
    'Content-Type': 'application/json',
  },
})

http.interceptors.request.use(async (config) => {
  await domainManager.initialize()
  config.baseURL = domainManager.getCurrentUrl() || import.meta.env.VITE_SERVER_BASEURL
  const userStore = useUserStore()
  if (userStore.token) {
    config.headers.Authorization = `Bearer ${userStore.token}`
  }
  return config
})

http.interceptors.response.use(
  async (response) => {
    const data = response.data
    // code: 1 表示业务成功
    if (data.code === 1) {
      return data
    }

    // code: 1002 需要实名认证
    if (data.code === 1002) {
      realnameJump(data.message)
      return Promise.reject(data)
    }

    // code: 1001 token失效或未登录
    if (data.code === 1001) {
      const userStore = useUserStore()
      userStore.logout()
      if (!response.config.hideErrorToast) {
        await showModal(data.message || '请求错误', {
          showCancel: false,
        })
      }
      uni.reLaunch({
        url: loginRoute,
      })
      return Promise.reject(data)
    }

    // 其他业务错误
    if (!response.config.hideErrorToast) {
      await showModal(data.message || '请求错误', { showCancel: false })
    }
    return Promise.reject(data)
  },
  async (error) => {
    const config = error.config

    // 对网络错误或服务器端错误进行重试
    if (config && !error.response) {
      const result = domainManager.markCurrentAsFaulty(config.baseURL)
      if (result) {
        if (!domainManager.areAllDomainsFaulty()) {
          console.log(`Request failed, retrying with ${domainManager.getCurrentUrl()}`)
          return http(config)
        }
      }
    }

    const data = error.response?.data
    const message = (data as any)?.message || error.message || '请求失败'
    !config?.hideErrorToast && showModal(message, { showCancel: false })
    return Promise.reject(error)
  },
)

export { http }
